/**
 * Multi-Account Store
 *
 * Allows users to stay signed in to multiple River accounts simultaneously
 * and switch between them — similar to Notion's workspace switcher or
 * Gmail's account switcher.
 *
 * Storage strategy:
 *   - Active account tokens remain in the standard localStorage keys
 *     (authToken, id_token, refresh_token) so the rest of the app never needs
 *     to change.
 *   - Each additional saved account is stored under a prefixed key:
 *     `river_account_<userId>` → JSON blob with tokens + cached user info.
 *   - An index of all saved account user IDs is kept under `river_accounts`.
 *
 * Switching accounts:
 *   1. Persist the current tokens back under the current userId slot.
 *   2. Write the target account's tokens into the active slots.
 *   3. Reload the page — the app bootstraps as if the new user just logged in.
 */

import { create } from 'zustand';
import { parseJwtPayload } from '../utils/jwtUtils';
import { createFileLogger } from '../utils/logger';
import { API_BASE_URL } from '../api/config';
import useUserStore from './userStore';

const log = createFileLogger('MULTI_ACCOUNT_STORE');

const ACCOUNTS_INDEX_KEY = 'river_accounts';
const ACCOUNT_PREFIX = 'river_account_';

/**
 * Unique per-page-load identifier, used to tell the initiating tab's own
 * 'river_account_switch' BroadcastChannel message apart from other tabs'.
 * BroadcastChannel delivers messages to every OTHER listener registered on
 * the same channel name within the browsing context, including a second
 * listener living in the SAME tab (e.g. App.tsx's cross-tab reload guard) —
 * it only skips the exact object instance that called postMessage(). Without
 * this guard, the initiating tab would reload itself on its own broadcast
 * and race its own resolveLandingDesktopRoute() navigation, landing back on
 * whatever URL was already in the address bar instead of the new account's
 * Desktop. See App.tsx's 'river_account_switch' listener.
 */
export const ACCOUNT_SWITCH_TAB_ID: string =
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

export interface SavedAccount {
  userId: string;
  email: string;
  name: string;
  /** Social login picture URL from JWT (Google, etc.) */
  picture?: string;
  /**
   * Custom River avatar: either a 24-char file entity ID or an external URL.
   * When rendering, generate the file URL using this account's own authToken
   * (not the currently active account's token) to avoid 403s on cross-account loads.
   */
  avatarEntityId?: string;
  /** ISO timestamp when this slot was last active */
  lastActiveAt: string;
  tokens: {
    authToken: string;
    id_token?: string;
    refresh_token?: string;
  };
}

/**
 * Resolve the best available avatar URL for a saved account.
 * Uses the account's own authToken so River-hosted file avatars load correctly
 * regardless of which account is currently active.
 */
export function resolveAccountAvatarUrl(account: SavedAccount): string | null {
  if (account.avatarEntityId) {
    if (account.avatarEntityId.match(/^[0-9a-fA-F]{24}$/)) {
      return `${API_BASE_URL}/files/read/${account.avatarEntityId}?token=${account.tokens.authToken}&size=full`;
    }
    if (account.avatarEntityId.startsWith('http://') || account.avatarEntityId.startsWith('https://')) {
      return account.avatarEntityId;
    }
  }
  return account.picture || null;
}

interface MultiAccountState {
  accounts: SavedAccount[];

  /** Load all saved accounts from localStorage into store */
  loadAccounts: () => void;

  /** Persist current active session as a saved account slot */
  saveCurrentAccount: () => void;

  /** Switch to a different saved account (reload will occur) */
  switchToAccount: (userId: string) => Promise<void>;

  /** Add a freshly-authenticated account (call after a secondary login completes) */
  addAccount: (tokens: { authToken: string; id_token?: string; refresh_token?: string }) => void;

  /** Remove a saved account (does NOT log out if it is the active one) */
  removeAccount: (userId: string) => void;

  /** Returns the userId of the currently active account, or null */
  getActiveUserId: () => string | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function loadIndex(): string[] {
  try {
    const raw = localStorage.getItem(ACCOUNTS_INDEX_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveIndex(ids: string[]): void {
  localStorage.setItem(ACCOUNTS_INDEX_KEY, JSON.stringify(ids));
}

function loadSlot(userId: string): SavedAccount | null {
  try {
    const raw = localStorage.getItem(`${ACCOUNT_PREFIX}${userId}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveSlot(account: SavedAccount): void {
  const key = `${ACCOUNT_PREFIX}${account.userId}`;
  const value = JSON.stringify(account);

  try {
    localStorage.setItem(key, value);
  } catch (err) {
    if (!(err instanceof DOMException) || err.name !== 'QuotaExceededError') {
      throw err;
    }

    // Quota exceeded — evict the oldest saved account slot(s) and retry once.
    log.warn('[saveSlot] localStorage quota exceeded, evicting oldest account slots');

    const ids = loadIndex();
    // Load all slots with their lastActiveAt so we can sort by age.
    const slots = ids
      .filter(id => id !== account.userId)
      .map(id => ({ id, slot: loadSlot(id) }))
      .filter((entry): entry is { id: string; slot: SavedAccount } => entry.slot !== null)
      .sort((a, b) => new Date(a.slot.lastActiveAt).getTime() - new Date(b.slot.lastActiveAt).getTime());

    // Remove oldest slots until we free enough space or run out.
    for (const { id } of slots) {
      removeSlot(id);
      saveIndex(ids.filter(existingId => existingId !== id));
      try {
        localStorage.setItem(key, value);
        return;
      } catch {
        // Still over quota — keep evicting.
      }
    }

    log.warn('[saveSlot] Could not free enough localStorage space to save account slot, skipping');
  }
}

function removeSlot(userId: string): void {
  localStorage.removeItem(`${ACCOUNT_PREFIX}${userId}`);
}

function extractUserInfo(tokens: { authToken: string; id_token?: string; refresh_token?: string }): { userId: string; email: string; name: string; picture?: string } | null {
  // Prefer id_token for richer user info; fall back to authToken
  const infoToken = tokens.id_token || tokens.authToken;
  const payload = parseJwtPayload(infoToken);
  if (!payload?.sub) return null;

  // Also check authToken for the canonical sub
  const authPayload = parseJwtPayload(tokens.authToken);
  const userId = authPayload?.sub || payload.sub;

  return {
    userId,
    email: payload.email || authPayload?.email || '',
    name: payload.name || authPayload?.name || payload.email || userId,
    picture: payload.picture || authPayload?.picture,
  };
}

/** Read the current user's avatar field from userStore without triggering a React re-render. */
function getUserStoreAvatar(): string | undefined {
  try {
    return useUserStore.getState().profile?.skeleton?.avatar || undefined;
  } catch {
    return undefined;
  }
}

// ── Store ─────────────────────────────────────────────────────────────────────

export const useMultiAccountStore = create<MultiAccountState>((set, get) => ({
  accounts: [],

  loadAccounts: () => {
    const ids = loadIndex();
    const accounts: SavedAccount[] = [];
    for (const id of ids) {
      const slot = loadSlot(id);
      if (slot) accounts.push(slot);
    }
    // Sort most-recently-active first
    accounts.sort((a, b) => new Date(b.lastActiveAt).getTime() - new Date(a.lastActiveAt).getTime());
    set({ accounts });
    log.info(`[loadAccounts] Loaded ${accounts.length} saved accounts`);
  },

  saveCurrentAccount: () => {
    const authToken = localStorage.getItem('authToken');
    const id_token = localStorage.getItem('id_token') || undefined;
    const refresh_token = localStorage.getItem('refresh_token') || undefined;

    if (!authToken) return;

    const info = extractUserInfo({ authToken, id_token, refresh_token });
    if (!info) return;

    // Capture the custom River avatar entity ID from userStore, if set.
    // We store the raw value (entity ID or external URL) so we can later
    // generate the correct file URL using this account's own token.
    const avatarEntityId: string | undefined = getUserStoreAvatar();

    const account: SavedAccount = {
      ...info,
      avatarEntityId,
      lastActiveAt: new Date().toISOString(),
      tokens: { authToken, id_token, refresh_token },
    };

    saveSlot(account);

    // Update index
    const ids = loadIndex();
    if (!ids.includes(info.userId)) {
      ids.push(info.userId);
      saveIndex(ids);
    }

    // Update in-memory list
    const { accounts } = get();
    const updated = accounts.filter(a => a.userId !== info.userId);
    updated.unshift(account);
    set({ accounts: updated });

    log.info(`[saveCurrentAccount] Saved account for ${info.email}`);
  },

  switchToAccount: async (targetUserId: string) => {
    const { saveCurrentAccount } = get();

    // 1. Persist current session so we can come back to it
    saveCurrentAccount();

    // 2. Load target slot
    const target = loadSlot(targetUserId);
    if (!target) {
      log.error(`[switchToAccount] No saved tokens for userId=${targetUserId}`);
      return;
    }

    // 3. Notify OTHER tabs BEFORE writing tokens so they can reload cleanly
    //    instead of hitting 403s from the wrong token. tabId lets listeners
    //    (App.tsx) ignore this tab's own message — this tab navigates itself
    //    via resolveLandingDesktopRoute() below instead of a blind reload.
    try {
      const bc = new BroadcastChannel('river_account_switch');
      bc.postMessage({ type: 'ACCOUNT_SWITCHING', tabId: ACCOUNT_SWITCH_TAB_ID });
      bc.close();
    } catch {
      // BroadcastChannel not available (old browsers / some Electron builds) — safe to ignore
    }

    // 4. Write target tokens as the active session
    localStorage.setItem('authToken', target.tokens.authToken);
    if (target.tokens.id_token) {
      localStorage.setItem('id_token', target.tokens.id_token);
    } else {
      localStorage.removeItem('id_token');
    }
    if (target.tokens.refresh_token) {
      localStorage.setItem('refresh_token', target.tokens.refresh_token);
    } else {
      localStorage.removeItem('refresh_token');
    }

    // 5. Mark this account as last active
    const updatedSlot: SavedAccount = {
      ...target,
      lastActiveAt: new Date().toISOString(),
    };
    saveSlot(updatedSlot);

    log.info(`[switchToAccount] Switched to ${target.email}, redirecting...`);

    // 6. Full page navigation to the new account's landing Desktop — resets
    // all in-memory state cleanly, same as a fresh login.
    try {
      const { resolveLandingDesktopRoute } = await import('../services/spaceHelpers');
      window.location.href = await resolveLandingDesktopRoute(target.tokens.authToken);
    } catch (err) {
      log.error('[switchToAccount] Failed to resolve landing Desktop, reloading in place:', err);
      window.location.reload();
    }
  },

  addAccount: (tokens) => {
    const info = extractUserInfo(tokens);
    if (!info) {
      log.error('[addAccount] Could not extract user info from tokens');
      return;
    }

    const account: SavedAccount = {
      ...info,
      lastActiveAt: new Date().toISOString(),
      tokens,
    };

    saveSlot(account);

    const ids = loadIndex();
    if (!ids.includes(info.userId)) {
      ids.push(info.userId);
      saveIndex(ids);
    }

    const { accounts } = get();
    const updated = accounts.filter(a => a.userId !== info.userId);
    updated.push(account);
    set({ accounts: updated });

    log.info(`[addAccount] Added account for ${info.email}`);
  },

  removeAccount: (userId: string) => {
    removeSlot(userId);

    const ids = loadIndex().filter(id => id !== userId);
    saveIndex(ids);

    const { accounts } = get();
    set({ accounts: accounts.filter(a => a.userId !== userId) });

    log.info(`[removeAccount] Removed account ${userId}`);
  },

  getActiveUserId: () => {
    const authToken = localStorage.getItem('authToken');
    if (!authToken) return null;
    const payload = parseJwtPayload(authToken);
    return payload?.sub || null;
  },
}));
